import {
  CheckCircleOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import {
  useGetReturnPartNumberByGroup,
  useReturnPartNumbeByGroup,
} from "../../../query/WarehouseQuery";
import { notify } from "../../../utils/toast";
import { IPartNumberRestock } from "../../../models/Warehouse";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

const { Title, Text } = Typography;

interface IModalRestockWarehouse {
  open: boolean;
  onClose: () => void;
  groupId: number | null;
}

const ModalRestockWarehouse = ({
  open,
  onClose,
  groupId,
}: IModalRestockWarehouse) => {
  const warehouse = useSelector((root: RootState) => root.warehouse.locations);
  const { data, isLoading } = useGetReturnPartNumberByGroup(
    open ? groupId : null,
  );
  const restockPartNumber = useReturnPartNumbeByGroup();

  const [selectedRestockIds, setSelectedRestockIds] = useState<number[]>([]);
  const [ngLocationsByItem, setNgLocationsByItem] = useState<
    Record<number, number | null>
  >({});

  const hasNGItems =
    data?.partNumberRestocks?.some(
      (item) =>
        Number(item.quantity) > 0 && item.qtyNG && Number(item.qtyNG) > 0,
    ) || false;

  const itemsWithNG =
    data?.partNumberRestocks?.filter((item) => Number(item.qtyNG || 0) > 0) ||
    [];

  const ngWarehouse = warehouse.find((w) => w.isWarehouseNG === true);

  const handleClose = () => {
    setSelectedRestockIds([]);
    setNgLocationsByItem({});
    onClose();
  };

  const handleSelectRestock = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRestockIds((prev) => [...prev, id]);
    } else {
      setSelectedRestockIds((prev) => prev.filter((itemId) => itemId !== id));
      setNgLocationsByItem((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  const handleSelectAllRestock = (
    checked: boolean,
    groupData: IPartNumberRestock[],
  ) => {
    if (checked) {
      const allEligibleIds = groupData
        .filter((item) => item.status === "PENDING")
        .map((item) => item.id);
      setSelectedRestockIds(allEligibleIds);
    } else {
      setSelectedRestockIds([]);
      setNgLocationsByItem({});
    }
  };

  const handleConfirmRestock = async () => {
    if (hasNGItems) {
      const selectedNGItems = itemsWithNG.filter((item) =>
        selectedRestockIds.includes(item.id),
      );
      const missingLocations = selectedNGItems.filter(
        (item) => !ngLocationsByItem[item.id],
      );
      if (missingLocations.length > 0) {
        notify.error(
          `Vui lòng chọn vị trí NG cho ${missingLocations.length} item có hàng lỗi`,
        );
        return;
      }
    }

    try {
      await restockPartNumber.mutateAsync(ngLocationsByItem);
      notify.success(`Đã xác nhận hoàn trả ${selectedRestockIds.length} item`);
      setSelectedRestockIds([]);
      setNgLocationsByItem({});
      handleClose();
    } catch (error: any) {
      notify.error(
        error?.response?.data?.message || "Không thể xác nhận hoàn trả",
      );
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Tag color="gold">Chờ xác nhận</Tag>;
      case "RETURNED":
        return <Tag color="blue">Đã hoàn trả</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getColumns = (
    groupData: IPartNumberRestock[],
  ): TableColumnsType<IPartNumberRestock> => [
    {
      title: () => {
        const eligibleItems = groupData.filter(
          (item) => item.status === "PENDING",
        );
        const allSelected =
          eligibleItems.length > 0 &&
          eligibleItems.every((item) => selectedRestockIds.includes(item.id));

        return (
          <Checkbox
            checked={allSelected}
            indeterminate={
              selectedRestockIds.length > 0 &&
              selectedRestockIds.length < eligibleItems.length
            }
            onChange={(e) =>
              handleSelectAllRestock(e.target.checked, groupData)
            }
            disabled={eligibleItems.length === 0}
          >
            Chọn
          </Checkbox>
        );
      },
      key: "checkbox",
      align: "center",
      width: 80,
      render: (_: any, record: IPartNumberRestock) => {
        const canSelect = record.status === "PENDING";

        return (
          <Checkbox
            checked={selectedRestockIds.includes(record.id)}
            onChange={(e) => handleSelectRestock(record.id, e.target.checked)}
            disabled={!canSelect}
          />
        );
      },
    },
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_: unknown, __: any, index: number) => index + 1,
    },
    {
      title: "Part Number",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
      width: 150,
      render: (text: string) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: "Mô tả",
      dataIndex: ["partNumber", "description"],
      key: "description",
      width: 200,
    },
    {
      title: "Nhà sản xuất",
      dataIndex: ["partNumber", "manufacturer"],
      key: "manufacturer",
      width: 150,
      align: "center",
    },
    {
      title: "Kho",
      dataIndex: ["partNumber", "location", "warehouse", "warehouseCode"],
      key: "warehouse",
      width: 100,
      align: "center",
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Vị trí",
      dataIndex: ["partNumber", "location", "code"],
      key: "location",
      width: 120,
      align: "center",
      render: (text: string) => (
        <Space direction="vertical" size={0}>
          <Tag color="green" icon={<EnvironmentOutlined />}>
            {text}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Số lượng OK",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "center",
      render: (value: string) => (
        <Text strong style={{ color: "#fa8c16", fontSize: 16 }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Số lượng NG",
      dataIndex: "qtyNG",
      key: "qtyNG",
      width: 140,
      align: "center",
      render: (value: string, record: IPartNumberRestock) => {
        const isSelected = selectedRestockIds.includes(record.id);
        const hasNG = Number(value || 0) > 0;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "center",
            }}
          >
            <Text strong style={{ color: "red", fontSize: 16 }}>
              {value}
            </Text>
            {hasNG && isSelected && (
              <div style={{ width: "100%", minWidth: 140 }}>
                {ngWarehouse && ngWarehouse.warehouseLocations.length > 0 ? (
                  <Select
                    value={ngLocationsByItem[record.id] || undefined}
                    onChange={(value) =>
                      setNgLocationsByItem((prev) => ({
                        ...prev,
                        [record.id]: value || null,
                      }))
                    }
                    placeholder="Chọn vị trí NG"
                    style={{ width: "100%" }}
                    options={ngWarehouse.warehouseLocations.map((loc) => ({
                      label: loc.code,
                      value: loc.id,
                    }))}
                  />
                ) : (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    Không có vị trí
                  </Text>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Đơn vị",
      dataIndex: ["partNumber", "unit"],
      key: "unit",
      width: 80,
      align: "center",
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status: string) => getStatusTag(status),
    },
    {
      title: "Ngày hoàn trả",
      dataIndex: "returnedAt",
      key: "returnedAt",
      width: 150,
      align: "center",
      render: (date: string | null) =>
        date ? (
          <Text>{dayjs(date).format("DD/MM/YYYY HH:mm")}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={1600}
      footer={
        selectedRestockIds.length > 0 ? (
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={handleClose}>Hủy</Button>
              <Popconfirm
                title="Xác nhận hoàn trả"
                description={`Bạn có chắc chắn muốn xác nhận hoàn trả ${selectedRestockIds.length} item đã chọn?`}
                onConfirm={handleConfirmRestock}
                okText="Xác nhận"
                cancelText="Hủy"
              >
                <Button type="primary" icon={<CheckCircleOutlined />}>
                  Xác nhận hoàn trả ({selectedRestockIds.length})
                </Button>
              </Popconfirm>
            </Space>
          </div>
        ) : null
      }
      closeIcon={<CloseOutlined />}
      title={
        <Space>
          <RollbackOutlined style={{ fontSize: 20, color: "#1890ff" }} />
          <Text>Chi tiết hoàn trả kho</Text>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        {data &&
          data.partNumberRestocks &&
          data.partNumberRestocks.length > 0 && (
            <div style={{ padding: "16px 0" }}>
              <div key={data.id} style={{ marginBottom: 24 }}>
                <Card
                  style={{ marginBottom: 16 }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Descriptions
                    bordered
                    column={{ xs: 1, sm: 2, md: 3 }}
                    size="small"
                  >
                    <Descriptions.Item label="Tên nhóm" span={2}>
                      <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                        {data.name}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="AWB">
                      <Tag color="blue" style={{ fontSize: 14 }}>
                        {data.awb}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tổng số linh kiện" span={3}>
                      <Text strong style={{ fontSize: 14, color: "#52c41a" }}>
                        {data.partNumberRestocks.length} linh kiện
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {hasNGItems && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 12,
                      backgroundColor: "#fff7e6",
                      borderRadius: 4,
                      border: "1px solid #ffd591",
                    }}
                  >
                    <Text style={{ fontSize: 12, color: "#ff7a45" }}>
                      💡 Tip: Tick vào mã hàng để chọn vị trí kho hàng lỗi (NG)
                    </Text>
                  </div>
                )}

                <Card
                  title={
                    <Space>
                      <FileTextOutlined />
                      <Title level={5} style={{ margin: 0 }}>
                        Danh sách linh kiện hoàn trả
                      </Title>
                    </Space>
                  }
                  styles={{ body: { padding: 0 } }}
                >
                  <Table
                    columns={getColumns(data.partNumberRestocks)}
                    dataSource={data.partNumberRestocks}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 1400, y: 400 }}
                    size="small"
                    summary={(pageData) => {
                      const totalQuantity = pageData.reduce(
                        (sum, record) => sum + Number(record.quantity || 0),
                        0,
                      );
                      const pendingCount = pageData.filter(
                        (r) => r.status === "PENDING",
                      ).length;
                      const completedCount = pageData.filter(
                        (r) => r.status === "COMPLETED",
                      ).length;
                      const returnedCount = pageData.filter(
                        (r) => r.status === "RETURNED",
                      ).length;

                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} />
                            <Table.Summary.Cell
                              index={1}
                              colSpan={5}
                              align="right"
                            >
                              <Text strong>Tổng cộng hoàn kho:</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                              <Text
                                strong
                                style={{ color: "#fa8c16", fontSize: 16 }}
                              >
                                {totalQuantity}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} colSpan={3}>
                              <Space>
                                <Tag color="gold">Chờ: {pendingCount}</Tag>
                                <Tag color="green">
                                  Hoàn thành: {completedCount}
                                </Tag>
                                <Tag color="blue">Đã trả: {returnedCount}</Tag>
                              </Space>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
                  />
                </Card>
              </div>
            </div>
          )}

        {(!data ||
          !data.partNumberRestocks ||
          data.partNumberRestocks.length === 0) && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#999",
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>Không có dữ liệu hoàn trả</div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ModalRestockWarehouse;
